import { crispService } from './crisp-service';
import { openCrispChat } from './consent/crisp';
import { hasAnalyticsConsent, hasMarketingConsent } from './consent/store';

/**
 * Cross-surface "open Crisp" intent handler.
 *
 * The side-panel Support row (running inside the Chrome extension on
 * WhatsApp Web) opens `https://app.blueticks.co/?openCrisp=1` in a new
 * tab. localStorage is per-origin so we can't hand the intent across
 * directly; instead we encode it in the URL.
 *
 * On the web-app side we want to:
 *   1. Capture the query param ONCE at boot — before any router /
 *      login-redirect logic erases it from the URL — and stash it in
 *      sessionStorage so the intent survives login.
 *   2. Strip the param from the URL so a refresh after-the-fact
 *      doesn't re-open Crisp.
 *   3. Once the user is authenticated AND Crisp has finished loading,
 *      open the chat and clear the flag so it never fires twice.
 *
 * Two small functions cover that lifecycle:
 *
 *   - `captureOpenCrispIntent()`  — call once at app boot.
 *   - `consumeOpenCrispIntent()`  — call after the user is identified.
 *
 * Both are safe to call in any context (extension content scripts,
 * server-side render placeholders, etc.) — they no-op if there's no
 * window/storage. Calling either from the extension surface is a
 * no-op because the query param won't be present there.
 */

const STORAGE_KEY = 'bt:open-crisp-after-load';
const QUERY_PARAM = 'openCrisp';

/** Read the `?openCrisp=1` flag (if present), stash it in
 *  sessionStorage so it survives login redirects, then remove it from
 *  the URL so a manual refresh doesn't reopen the chat indefinitely.
 *
 *  Also widens to `localStorage` as a fallback because some OAuth
 *  providers (Google, Apple) round-trip through a different origin
 *  and re-create the session storage on return — localStorage is
 *  per-origin so it survives the same-origin reload, and we clear it
 *  the moment the chat opens (or after a 30 s safety cap).
 *
 *  Safe to call multiple times; the URL cleanup is idempotent. */
export const captureOpenCrispIntent = (): void => {
  if (typeof window === 'undefined') return;
  try {
    const url = new URL(window.location.href);
    if (url.searchParams.get(QUERY_PARAM) !== '1') return;
    window.sessionStorage.setItem(STORAGE_KEY, '1');
    try {
      window.localStorage.setItem(
        STORAGE_KEY,
        // Timestamped so a stale flag from a long-ago session can be
        // recognised + ignored by the consumer.
        String(Date.now()),
      );
    } catch {
      /* localStorage may be blocked in incognito/strict modes — ok. */
    }
    url.searchParams.delete(QUERY_PARAM);
    const cleaned = url.pathname + (url.search ? url.search : '') + url.hash;
    window.history.replaceState(window.history.state, '', cleaned);
  } catch {
    /* No-op outside browser context. */
  }
};

/** Max age (ms) a localStorage-stored "open Crisp" intent is treated as
 *  fresh. Beyond this the flag is considered stale and ignored — keeps
 *  a 6-months-old session from popping the chat on a casual return. */
const INTENT_MAX_AGE_MS = 30 * 60 * 1000;

/** True if either storage layer carries a fresh open-Crisp intent. */
const hasPendingIntent = (): boolean => {
  if (typeof window === 'undefined') return false;
  try {
    if (window.sessionStorage.getItem(STORAGE_KEY) === '1') return true;
  } catch {}
  try {
    const stored = window.localStorage.getItem(STORAGE_KEY);
    if (!stored) return false;
    const ts = Number(stored);
    if (Number.isNaN(ts)) return false;
    return Date.now() - ts < INTENT_MAX_AGE_MS;
  } catch {
    return false;
  }
};

/** Wipe the intent from every storage layer once the chat has been
 *  opened so a refresh doesn't pop it again. */
const clearIntent = (): void => {
  if (typeof window === 'undefined') return;
  try { window.sessionStorage.removeItem(STORAGE_KEY); } catch {}
  try { window.localStorage.removeItem(STORAGE_KEY); } catch {}
};

/** If a pending open-Crisp intent is stored, open the Crisp chat and
 *  clear the flag. Bails out cleanly if Crisp hasn't initialised yet —
 *  the caller decides whether to retry. */
/** One load per page view. Only ever started when the visitor has granted a category. */
let consentCrispRequested = false;

export const consumeOpenCrispIntent = (): boolean => {
  if (typeof window === 'undefined') return false;
  try {
    if (!hasPendingIntent()) return false;
    if (!crispService.isAvailable) {
      // A ?openCrisp=1 URL is not a click on this page, so Crisp (l.js) loads only when the
      // visitor has granted analytics or marketing. Without consent the intent stays pending.
      const consented = hasAnalyticsConsent() || hasMarketingConsent();
      if (!consented) return false;
      if (!consentCrispRequested) {
        consentCrispRequested = true;
        void openCrispChat().then(ok => { if (ok) clearIntent(); });
      }
      return false;
    }
    crispService.openChat();
    clearIntent();
    return true;
  } catch {
    return false;
  }
};

/** Convenience wrapper: try once now, otherwise retry on a short
 *  interval for up to ~30 s (Crisp script load races auth + the
 *  marketing site's lazy chunks). Stops as soon as either the chat
 *  opens or the cap is hit. Repeat calls are safe — earlier intervals
 *  get cleared by the same module-scope guard. */
let activeIntervalId: number | null = null;
export const consumeOpenCrispIntentWithRetry = (): void => {
  if (typeof window === 'undefined') return;
  if (!hasPendingIntent()) return;
  if (consumeOpenCrispIntent()) return;
  if (activeIntervalId !== null) return;
  let attempts = 0;
  activeIntervalId = window.setInterval(() => {
    attempts++;
    if (consumeOpenCrispIntent() || attempts >= 150) {
      if (activeIntervalId !== null) {
        window.clearInterval(activeIntervalId);
        activeIntervalId = null;
      }
    }
  }, 200);
};

// Module-load side-effect: capture the URL param as early as possible.
// Importing this module from anywhere on the web app guarantees the
// param is stashed BEFORE any router redirect / login guard can wipe
// the URL. WebsiteComponent still calls captureOpenCrispIntent()
// explicitly as a belt-and-braces — both are idempotent.
try {
  captureOpenCrispIntent();
} catch {
  /* Module-level capture is best-effort; explicit useEffect picks up
     the slack in environments where this branch can't run yet. */
}
